import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Library, Database, Search, Image as ImageIcon, Inbox, Settings,
  ArrowRight, Loader2, AlertCircle, CheckCircle2, XCircle, Pencil, Zap, Check,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useAdminTheme, tc } from './theme';

// ---- Reusable phase placeholder ---------------------------------------------
function PhasePlaceholder({ eyebrow, title, description, phase = 'Phase 1', icon: Icon, children }) {
  const { theme } = useAdminTheme();
  return (
    <div className="space-y-6">
      <div>
        <p className={`text-xs uppercase tracking-widest font-bold ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>{eyebrow}</p>
        <h1 className={`text-3xl font-extrabold mt-1 ${tc.textPrimary(theme)}`}>{title}</h1>
        <p className={`mt-2 max-w-2xl ${tc.textMuted(theme)}`}>{description}</p>
      </div>

      <div className={`rounded-2xl p-5 flex items-start gap-3 ${theme === 'light' ? 'bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200' : 'bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20'}`}>
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg shadow-fuchsia-500/30 shrink-0">
          <Icon size={18} />
        </div>
        <div>
          <p className={`font-bold ${tc.textPrimary(theme)}`}>{phase} delivers this screen</p>
          <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>
            The data model and API are live in Phase 0/1. {phase} fills the editor UI with inline{' '}
            <span className={theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}>AI Generate</span> /{' '}
            <span className={theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}>Improve</span> buttons and SEO controls.
          </p>
        </div>
      </div>

      {children}
    </div>
  );
}

// ---- Content Library --------------------------------------------------------
export function AdminV2Library() {
  const { theme } = useAdminTheme();
  return (
    <PhasePlaceholder
      eyebrow="Admin v2"
      title="Content Library"
      description="Blog posts and Resources — long-form content that lives outside the public pages. Bulk AI generation, rich text editor, cover images and SEO metadata per article."
      phase="Phase 3"
      icon={Library}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={`rounded-2xl p-5 ${tc.cardBg(theme)}`}>
          <h3 className={`font-bold ${tc.textPrimary(theme)}`}>Blog</h3>
          <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>Currently editable in legacy admin →</p>
          <Link to="/admin/blog" className={`inline-flex items-center gap-1 text-sm mt-3 font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
            Open in legacy admin <ArrowRight size={14} />
          </Link>
        </div>
        <div className={`rounded-2xl p-5 ${tc.cardBg(theme)}`}>
          <h3 className={`font-bold ${tc.textPrimary(theme)}`}>Resources</h3>
          <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>Currently editable in legacy admin →</p>
          <Link to="/admin/resources" className={`inline-flex items-center gap-1 text-sm mt-3 font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
            Open in legacy admin <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </PhasePlaceholder>
  );
}

// ---- Master Data ------------------------------------------------------------
export function AdminV2MasterData() {
  const { theme } = useAdminTheme();
  const groups = [
    { name: 'Services', desc: 'Service cards used across the site', legacy: null },
    { name: 'Features', desc: 'Feature list items', legacy: null },
    { name: 'FAQs', desc: 'Q&A entries — AI answers in Phase 3', legacy: '/admin/faqs' },
    { name: 'Reviews', desc: 'Customer testimonials', legacy: '/admin/reviews' },
    { name: 'CRM Logos', desc: 'Integration partner logos', legacy: '/admin/logos' },
    { name: 'Virtual Pricing', desc: 'DID / toll-free / UIFN pricing per country', legacy: '/admin/virtual-pricing' },
    { name: 'Regulations', desc: 'KYC + processing time per country', legacy: '/admin/regulations' },
  ];
  return (
    <PhasePlaceholder
      eyebrow="Admin v2"
      title="Master Data"
      description="Structured records that get rendered into multiple places on the site. Edit once, ripple everywhere."
      phase="Phase 2.2"
      icon={Database}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => (
          <div key={g.name} className={`rounded-2xl p-5 ${tc.cardBg(theme)}`}>
            <h3 className={`font-bold ${tc.textPrimary(theme)}`}>{g.name}</h3>
            <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>{g.desc}</p>
            {g.legacy && (
              <Link to={g.legacy} className={`inline-flex items-center gap-1 text-sm mt-3 font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
                Open in legacy admin <ArrowRight size={14} />
              </Link>
            )}
          </div>
        ))}
      </div>
    </PhasePlaceholder>
  );
}

// ---- SEO Hub (live + Bulk SEO Regenerate) ----------------------------------
export function AdminV2SeoHub() {
  const { theme } = useAdminTheme();
  const [pages, setPages] = useState([]);
  const [seoMap, setSeoMap] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bulk regenerate state
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, current: '' });
  const [bulkErrors, setBulkErrors] = useState([]);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/admin-v2/pages');
      setPages(res.data || []);
      const seo = {};
      await Promise.all(
        (res.data || []).map(async (p) => {
          try {
            const s = await api.get(`/api/admin-v2/seo/${p.slug}`);
            seo[p.slug] = s.data;
          } catch (_) {
            seo[p.slug] = null;
          }
        }),
      );
      setSeoMap(seo);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await reload();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Bulk regenerate SEO meta for every page that is missing title OR
   * description. Calls Claude per-field per-page, then PUTs the SEO record.
   * Runs sequentially so the API key isn't blasted and progress is clear.
   */
  const bulkRegenerate = async ({ overwrite }) => {
    const targets = pages.filter((p) => {
      if (overwrite) return true;
      const s = seoMap[p.slug];
      return !s?.title || !s?.description;
    });
    if (!targets.length) {
      setBulkErrors(['Nothing to regenerate.']);
      return;
    }

    setBulkBusy(true);
    setBulkProgress({ done: 0, total: targets.length, current: '' });
    setBulkErrors([]);

    let done = 0;
    for (const p of targets) {
      setBulkProgress({ done, total: targets.length, current: p.name });
      try {
        const existing = seoMap[p.slug] || { page_slug: p.slug };
        // 1) title
        let title = existing.title || '';
        if (overwrite || !title) {
          const r = await api.post('/api/admin-v2/ai/generate', {
            prompt_key: 'seo_title',
            action: 'generate',
            current_text: '',
            variables: { page_name: p.name, primary_keyword: p.name },
          });
          title = r.data?.output?.trim() || title;
        }
        // 2) description
        let description = existing.description || '';
        if (overwrite || !description) {
          const r = await api.post('/api/admin-v2/ai/generate', {
            prompt_key: 'seo_description',
            action: 'generate',
            current_text: '',
            variables: { page_name: p.name, primary_keyword: p.name },
          });
          description = r.data?.output?.trim() || description;
        }
        // 3) save
        await api.put(`/api/admin-v2/seo/${p.slug}`, {
          ...existing,
          page_slug: p.slug,
          title,
          description,
          og_title: existing.og_title || title,
          og_description: existing.og_description || description,
          twitter_title: existing.twitter_title || title,
          twitter_description: existing.twitter_description || description,
        });
      } catch (e) {
        setBulkErrors((arr) => [...arr, `${p.name}: ${formatApiError(e)}`]);
      }
      done += 1;
      setBulkProgress({ done, total: targets.length, current: p.name });
    }
    setBulkBusy(false);
    await reload();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className={`text-xs uppercase tracking-widest font-bold ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>Admin v2</p>
          <h1 className={`text-3xl font-extrabold mt-1 ${tc.textPrimary(theme)}`}>SEO Hub</h1>
          <p className={`mt-2 max-w-2xl ${tc.textMuted(theme)}`}>
            Per-page meta title and description, canonical, Open Graph, Twitter card and Schema.org JSON-LD.
            Click any row to open the per-page editor, or use{' '}
            <span className={theme === 'light' ? 'text-fuchsia-600 font-semibold' : 'text-fuchsia-300 font-semibold'}>Bulk AI</span>{' '}
            to fill every page in one go.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={bulkBusy || loading}
            onClick={() => bulkRegenerate({ overwrite: false })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bulk-seo-fill-empty"
          >
            {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Fill empty SEO with AI
          </button>
          <button
            type="button"
            disabled={bulkBusy || loading}
            onClick={() => bulkRegenerate({ overwrite: true })}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${theme === 'light' ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50' : 'bg-white/5 border border-white/15 text-slate-200 hover:bg-white/10'}`}
            data-testid="bulk-seo-overwrite"
          >
            <Zap size={14} /> Regenerate ALL
          </button>
        </div>
      </div>

      {bulkBusy && (
        <div className={`rounded-2xl p-4 ${theme === 'light' ? 'bg-violet-50 border border-violet-200' : 'bg-violet-500/10 border border-violet-500/30'}`}>
          <div className="flex items-center justify-between text-sm">
            <span className={tc.textPrimary(theme)}>
              <Loader2 size={14} className="inline animate-spin mr-1" /> Generating SEO for{' '}
              <span className="font-semibold">{bulkProgress.current || '…'}</span>
            </span>
            <span className={tc.textMuted(theme)}>{bulkProgress.done} / {bulkProgress.total}</span>
          </div>
          <div className={`mt-2 h-2 w-full rounded-full overflow-hidden ${theme === 'light' ? 'bg-white' : 'bg-slate-900/60'}`}>
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all"
              style={{ width: `${bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {bulkErrors.length > 0 && (
        <div className={`rounded-xl px-4 py-3 text-sm ${theme === 'light' ? 'text-rose-700 bg-rose-50 border border-rose-200' : 'text-rose-300 bg-rose-500/10 border border-rose-500/30'}`}>
          <p className="font-semibold mb-1">Some pages failed:</p>
          <ul className="list-disc list-inside text-xs space-y-0.5">
            {bulkErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className={`rounded-xl p-4 ${tc.cardBg(theme)}`}>
          <p className={`text-xs uppercase tracking-widest font-bold ${tc.textMuted(theme)}`}>sitemap.xml</p>
          <a href="/sitemap.xml" target="_blank" rel="noreferrer" className={`font-mono text-sm break-all mt-1 inline-block ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
            /sitemap.xml
          </a>
          <p className={`text-xs mt-1 ${tc.textDim(theme)}`}>Generated dynamically from enabled pages + published blog/resources.</p>
        </div>
        <div className={`rounded-xl p-4 ${tc.cardBg(theme)}`}>
          <p className={`text-xs uppercase tracking-widest font-bold ${tc.textMuted(theme)}`}>robots.txt</p>
          <a href="/robots.txt" target="_blank" rel="noreferrer" className={`font-mono text-sm break-all mt-1 inline-block ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
            /robots.txt
          </a>
          <p className={`text-xs mt-1 ${tc.textDim(theme)}`}>Falls back to a sensible default; editable in Phase 2.2.</p>
        </div>
        <div className={`rounded-xl p-4 ${tc.cardBg(theme)}`}>
          <p className={`text-xs uppercase tracking-widest font-bold ${tc.textMuted(theme)}`}>Structured data</p>
          <p className={`text-sm mt-1 ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}`}>Schema.org JSON-LD</p>
          <p className={`text-xs mt-1 ${tc.textDim(theme)}`}>Per-page in the editor + bulk-generation in Phase 2.2.</p>
        </div>
      </div>

      {error && (
        <div className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${theme === 'light' ? 'text-rose-700 bg-rose-50 border border-rose-200' : 'text-rose-300 bg-rose-500/10 border border-rose-500/30'}`}>
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {loading && !error && (
        <div className={`flex items-center gap-2 text-sm ${tc.textMuted(theme)}`}>
          <Loader2 size={16} className="animate-spin" /> Loading SEO coverage…
        </div>
      )}

      <div className={`rounded-2xl overflow-hidden ${tc.cardBg(theme)}`}>
        <table className="w-full text-sm">
          <thead className={`uppercase text-[10px] tracking-widest ${theme === 'light' ? 'bg-slate-100 text-slate-600' : 'bg-slate-900/80 text-slate-400'}`}>
            <tr>
              <th className="text-left px-4 py-3 font-bold">Page</th>
              <th className="text-left px-4 py-3 font-bold">Meta title</th>
              <th className="text-left px-4 py-3 font-bold">Meta description</th>
              <th className="text-left px-4 py-3 font-bold">Status</th>
              <th className="text-right px-4 py-3 font-bold">Edit</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => {
              const seo = seoMap[p.slug];
              const titleLen = seo?.title?.length || 0;
              const descLen = seo?.description?.length || 0;
              const filled = titleLen > 0 && descLen > 0;
              return (
                <tr key={p.slug} className={`border-t ${tc.divider(theme)} ${tc.hoverBg(theme)}`}>
                  <td className="px-4 py-3">
                    <p className={`font-semibold ${tc.textPrimary(theme)}`}>{p.name}</p>
                    <p className={`text-xs font-mono ${tc.textDim(theme)}`}>{p.public_path}</p>
                  </td>
                  <td className={`px-4 py-3 max-w-xs truncate ${tc.textMuted(theme)}`}>
                    {seo?.title || <span className={tc.textDim(theme)}>—</span>}
                    {titleLen > 0 && <span className={`ml-2 text-[10px] ${tc.textDim(theme)}`}>{titleLen} ch</span>}
                  </td>
                  <td className={`px-4 py-3 max-w-md truncate ${tc.textMuted(theme)}`}>
                    {seo?.description || <span className={tc.textDim(theme)}>—</span>}
                    {descLen > 0 && <span className={`ml-2 text-[10px] ${tc.textDim(theme)}`}>{descLen} ch</span>}
                  </td>
                  <td className="px-4 py-3">
                    {filled ? (
                      <span className={`inline-flex items-center gap-1 text-xs font-bold ${theme === 'light' ? 'text-emerald-700' : 'text-emerald-300'}`}>
                        <CheckCircle2 size={12} /> Filled
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 text-xs font-bold ${theme === 'light' ? 'text-rose-700' : 'text-rose-300'}`}>
                        <XCircle size={12} /> Missing
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/seo/${p.slug}`} className={`inline-flex items-center gap-1 text-xs font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
                      <Pencil size={12} /> Edit
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Media Library ---------------------------------------------------------
export function AdminV2Media() {
  return (
    <PhasePlaceholder
      eyebrow="Admin v2"
      title="Media Library"
      description="Image and file uploads with metadata, folders, alt text (AI-generated in Phase 2.4) and tagging."
      phase="Phase 2.4"
      icon={ImageIcon}
    />
  );
}

// ---- Inbox -----------------------------------------------------------------
export function AdminV2Inbox() {
  const { theme } = useAdminTheme();
  return (
    <PhasePlaceholder
      eyebrow="Admin v2"
      title="Inbox"
      description="Contact form leads, partner applications and number orders — all in one place."
      phase="Phase 3"
      icon={Inbox}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/admin/leads" className={`rounded-2xl p-5 hover:border-violet-500/40 transition-all block ${tc.cardBg(theme)}`}>
          <h3 className={`font-bold ${tc.textPrimary(theme)}`}>Contact leads</h3>
          <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>Form submissions → legacy admin</p>
          <p className={`text-xs mt-3 ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}`}>Open <ArrowRight size={12} className="inline" /></p>
        </Link>
        <Link to="/admin/partners" className={`rounded-2xl p-5 hover:border-violet-500/40 transition-all block ${tc.cardBg(theme)}`}>
          <h3 className={`font-bold ${tc.textPrimary(theme)}`}>Partner applications</h3>
          <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>Reseller / white-label requests</p>
          <p className={`text-xs mt-3 ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}`}>Open <ArrowRight size={12} className="inline" /></p>
        </Link>
        <Link to="/admin/orders" className={`rounded-2xl p-5 hover:border-violet-500/40 transition-all block ${tc.cardBg(theme)}`}>
          <h3 className={`font-bold ${tc.textPrimary(theme)}`}>Number orders</h3>
          <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>DID, toll-free, UIFN orders</p>
          <p className={`text-xs mt-3 ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}`}>Open <ArrowRight size={12} className="inline" /></p>
        </Link>
      </div>
    </PhasePlaceholder>
  );
}

// ---- AI Prompts (live) -----------------------------------------------------
export function AdminV2AiPrompts() {
  const { theme } = useAdminTheme();
  const [prompts, setPrompts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/admin-v2/ai/prompts')
      .then((res) => !cancelled && setPrompts(res.data || []))
      .catch((err) => !cancelled && setError(formatApiError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className={`text-xs uppercase tracking-widest font-bold ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>Admin v2</p>
        <h1 className={`text-3xl font-extrabold mt-1 ${tc.textPrimary(theme)}`}>AI Prompts</h1>
        <p className={`mt-2 max-w-2xl ${tc.textMuted(theme)}`}>
          Every <Sparkles size={12} className={`inline ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}`} /> Generate / Improve button in the
          admin uses one of these prompts. Tweak the system prompt and user template to change the tone
          or rules across the entire site without redeploying.
        </p>
      </div>

      <div className={`rounded-2xl p-4 text-sm ${theme === 'light' ? 'bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 text-slate-700' : 'bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 text-slate-300'}`}>
        Live: edit the prompt body and Claude immediately uses your version. Editing UI ships in Phase 2.2.
      </div>

      {error && (
        <div className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${theme === 'light' ? 'text-rose-700 bg-rose-50 border border-rose-200' : 'text-rose-300 bg-rose-500/10 border border-rose-500/30'}`}>
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {loading && !error && (
        <div className={`flex items-center gap-2 text-sm ${tc.textMuted(theme)}`}>
          <Loader2 size={16} className="animate-spin" /> Loading prompts…
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {prompts.map((p) => (
          <div key={p.id || p.key} className={`rounded-2xl p-5 ${tc.cardBg(theme)}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={`font-semibold ${tc.textPrimary(theme)}`}>{p.name}</p>
                <p className={`text-xs font-mono mt-0.5 ${tc.textDim(theme)}`}>{p.key}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-md ${theme === 'light' ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'bg-violet-500/15 text-violet-300 border border-violet-500/30'}`}>
                {p.output_format}
              </span>
            </div>
            <p className={`text-sm mt-2 ${tc.textMuted(theme)}`}>{p.description}</p>
            <details className="mt-3 group">
              <summary className={`text-xs cursor-pointer font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
                Show template
              </summary>
              <pre className={`mt-2 text-[11px] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap ${theme === 'light' ? 'text-slate-700 bg-slate-50 border border-slate-200' : 'text-slate-400 bg-slate-950/60 border border-white/10'}`}>
                {p.user_prompt_template}
              </pre>
            </details>
          </div>
        ))}
      </div>

      {!loading && !error && prompts.length === 0 && (
        <div className={`rounded-2xl border border-dashed p-10 text-center ${theme === 'light' ? 'border-slate-300 text-slate-500' : 'border-white/15 text-slate-400'}`}>
          No prompts seeded. Restart the backend to re-run `seed_ai_prompts`.
        </div>
      )}
    </div>
  );
}

// ---- Settings --------------------------------------------------------------
export function AdminV2Settings() {
  const { theme } = useAdminTheme();
  return (
    <PhasePlaceholder
      eyebrow="Admin v2"
      title="Settings"
      description="Site brand, contact details, integration keys (Anthropic, S3, Turnstile), admin users."
      phase="Phase 3"
      icon={Settings}
    >
      <Link to="/admin/settings" className={`inline-flex items-center gap-1 text-sm font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
        Use legacy settings for now <ArrowRight size={14} />
      </Link>
    </PhasePlaceholder>
  );
}
